<template>
  <van-popup :show="show" position="bottom" :style="{ height: '100%', width: '100%' }" @update:show="updateShow">
    <div v-if="optionsLoading" class="popup-loading">
      <van-loading type="spinner" />
      <span>加载中...</span>
    </div>
    <div v-else class="popup-content">
      <van-form @submit="handleSubmit">
        <van-field v-model="localForm.contactName" label="联系人名称" placeholder="请输入联系人名称" required />
        <van-field v-model="localForm.phone" label="手机号" placeholder="请输入手机号" type="tel" />
        <van-field v-model="localForm.otherContact" label="其他联系方式" placeholder="请输入其他联系方式" />
        
        <DictSelect
          v-model="localForm.source"
          name="source"
          label="来源"
          placeholder="请选择来源"
          :columns="consultation_source"
          required
          @confirm="(data) => { localForm.sourceText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.nearbySchoolId"
          name="nearbySchool"
          label="就近校区"
          placeholder="请选择就近校区"
          :columns="schoolList"
          :column-names="schoolFieldNames"
          required
          @confirm="(data) => { localForm.nearbySchoolText = data.label; localForm.nearbySchoolId = data.value }"
        />
        
        <DictSelect
          v-model="localForm.area"
          name="area"
          label="区域"
          placeholder="请选择区域"
          :columns="consultation_area"
          @confirm="(data) => { localForm.areaText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.grade"
          name="grade"
          label="年级"
          placeholder="请选择年级"
          :columns="grade_list"
          @confirm="(data) => { localForm.gradeText = data.label }"
        />
        
        <DictSelectWithSearch
          v-model="localForm.followUserId"
          name="follower"
          label="跟进人"
          placeholder="请选择跟进人"
          :columns="followerList"
          :column-names="{ text: 'nickName', value: 'userId' }"
          search-placeholder="搜索跟进人姓名"
          @confirm="(data) => { localForm.followerText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.studentType"
          name="studentType"
          label="学生类型"
          placeholder="请选择学生类型"
          :columns="student_type"
          @confirm="(data) => { localForm.studentTypeText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.quality"
          name="quality"
          label="线索质量"
          placeholder="请选择线索质量"
          :columns="common_level"
          @confirm="(data) => { localForm.qualityText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.type"
          name="type"
          label="线索归类"
          placeholder="请选择线索归类"
          :columns="typeOptions"
          :column-names="{ text: 'label', value: 'value' }"
          required
          @confirm="(data) => { localForm.typeText = data.label }"
        />

        <div class="subject-container">
          <div class="subject-header">
            <span>意向科目</span>
            <van-button type="link" size="small" @click.prevent="toggleAllSubjects">
              {{ localForm.subjects.length === course_subject.length ? '取消全选' : '全选' }}
            </van-button>
          </div>
          <van-checkbox-group v-model="localForm.subjects">
            <div class="subject-grid">
              <van-checkbox
                v-for="item in course_subject"
                :key="item.dictValue"
                :name="item.dictValue"
                class="subject-item"
                shape="round"
              >
                {{ item.dictLabel }}
              </van-checkbox>
            </div>
          </van-checkbox-group>
        </div>

        <van-field v-model="localForm.remark" label="备注" placeholder="请输入备注" type="textarea" rows="3" maxlength="200" show-word-limit />

        <div class="form-actions">
          <van-button type="default" block native-type="button" @click="closePopup">取消</van-button>
          <van-button type="primary" block native-type="submit" :loading="submitLoading">确定</van-button>
        </div>
      </van-form>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { showToast } from 'vant'
import { getSchoolDicts } from '@/api/dict'
import { getUserListByMenuPerms } from '@/api/user'
import { useDict, selectDictLabel } from '@/utils/dict'
import DictSelect from '@/components/DictSelect.vue'
import DictSelectWithSearch from '@/components/DictSelectWithSearch.vue'
import { getInitialConsultationForm } from '../formModels'
import { buildConsultationPayload, getConsultationFormError } from '../formSubmitters'
import { getUserInfo as getLocalUserInfo, setUserInfo } from '@/utils/authUtils'
import { getUserInfo } from '@/api/auth'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  submitLoading: {
    type: Boolean,
    default: false
  },
  initialName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:show', 'submit'])

const {
  consultation_source,
  consultation_area,
  grade_list,
  student_type,
  common_level,
  course_subject,
  marketing_activity_type,
  loadDicts
} = useDict(
  'consultation_source',
  'consultation_area',
  'grade_list',
  'student_type',
  'common_level',
  'course_subject',
  'marketing_activity_type'
)

const localForm = ref(getInitialConsultationForm())
const schoolList = ref([])
const followerList = ref([])
const schoolFieldNames = { text: 'schoolName', value: 'schoolId' }
const optionsLoaded = ref(false)
const optionsPromise = ref(null)
const optionsLoading = ref(false)
const userInfo = ref(null)

const typeOptions = computed(() => {
  const roles = userInfo.value?.roles || []
  const privilegedRoles = ['dspzy', 'marketing_director', 'superadmin', 'besAdmin', 'marketingDirector', 'yyfz']
  const hasPrivilegedRole = privilegedRoles.some(role => roles.includes(role))
  const allowedTypes = hasPrivilegedRole ? [1, 2, 3] : [2, 3]
  return (marketing_activity_type.value || [])
    .filter(item => allowedTypes.includes(Number(item.dictValue)))
    .map(item => ({
      label: item.dictLabel,
      value: item.dictValue
    }))
})

function cloneForm(form) {
  return {
    ...form,
    subjects: Array.isArray(form.subjects) ? [...form.subjects] : []
  }
}

async function ensureOptionsLoaded() {
  if (optionsLoaded.value) {
    return
  }
  if (optionsPromise.value) {
    await optionsPromise.value
    return
  }
  optionsLoading.value = true
  try {
    // 获取用户信息用于权限判断
    let localUserInfo = getLocalUserInfo()
    if (!localUserInfo) {
      localUserInfo = await getUserInfo()
      setUserInfo(localUserInfo)
    }
    userInfo.value = localUserInfo

    optionsPromise.value = Promise.all([
      loadDicts(),
      getSchoolDicts(),
      getUserListByMenuPerms({ perms: 'enrollment:consultationBook:list' })
    ])
    const [, schools, followers] = await optionsPromise.value
    schoolList.value = schools
    followerList.value = followers
    optionsLoaded.value = true
  } finally {
    optionsPromise.value = null
    optionsLoading.value = false
  }
}

function buildInitialForm() {
  const form = getInitialConsultationForm()
  form.contactName = props.initialName || ''
  if (schoolList.value.length === 1) {
    form.nearbySchoolId = String(schoolList.value[0].schoolId)
    form.nearbySchoolText = schoolList.value[0].schoolName
  }
  // 设置默认线索归类
  const roles = userInfo.value?.roles || []
  const privilegedRoles = ['dspzy', 'marketing_director', 'superadmin', 'besAdmin', 'marketingDirector', 'yyfz']
  const hasPrivilegedRole = privilegedRoles.some(role => roles.includes(role))
  const defaultType = hasPrivilegedRole ? 1 : 2
  form.type = String(defaultType)
  const typeItem = typeOptions.value.find(item => Number(item.value) === defaultType)
  form.typeText = typeItem ? typeItem.label : ''
  return form
}

async function initializePopup() {
  await ensureOptionsLoaded()
  localForm.value = buildInitialForm()
}

function updateShow(value) {
  emit('update:show', value)
}

function closePopup() {
  updateShow(false)
}

function handleSubmit() {
  const form = cloneForm(localForm.value)
  const error = getConsultationFormError(form)
  if (error) {
    showToast(error)
    return
  }
  emit('submit', buildConsultationPayload(form))
}

function toggleAllSubjects() {
  if (localForm.value.subjects.length === course_subject.value.length) {
    localForm.value.subjects = []
    return
  }
  localForm.value.subjects = course_subject.value.map(item => item.dictValue)
}

watch(() => props.show, value => {
  if (value) {
    initializePopup().catch(error => {
      showToast(error?.message || '加载线索表单失败')
      updateShow(false)
    })
  }
})
</script>

<style scoped>
.popup-content {
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

.popup-loading {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #666;
}

.form-actions {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

.form-actions .van-button {
  flex: 1;
  height: 44px;
  border-radius: 8px;
}

.subject-container {
  padding: 0 16px 16px;
  margin-top: -1px;
}

.subject-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 14px;
  color: #333;
}

.subject-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.subject-item {
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 8px 0;
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.subject-item .van-checkbox__label {
  padding: 0;
  margin-left: 4px;
}

.user-picker-popup {
  height: 80vh;
  overflow-y: auto;
}
</style>
